import { useEffect, useState } from "react";
import ModalComponent from "../../../Components/ModalComponent";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Upload,
} from "antd";
import { useTranslation } from "react-i18next";
import { CameraOutlined, UploadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  ToEditProfile,
  toGetCountries,
  toGetProfil,
  toGetTitles,
} from "../../../apis/actions/auth.actions";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../apis/store";
import { convertFileToBase64, showNotitfication } from "../../../utils/Utils";
import dayjs from "dayjs";
import { saudiCitiesAr, saudiCitiesEn } from "../../data/saudiCities";
import EditProfileOtp from "../EditProfileOtp";
import SaudiSvg from "../../../assets/saudi-svg.svg";

interface MyCarsEditProps {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  selectedData: any;
}

// Keep only digits and convert to 9-digit local SA number starting with 5
const sanitizeSaudiPhone = (raw: string): string => {
  if (!raw) return "";
  // 1) keep digits only
  let p = raw.replace(/\D/g, "");

  // 2) strip leading +966 or 00966 or 966
  if (p.startsWith("00966")) p = p.slice(5);
  if (p.startsWith("966")) p = p.slice(3);

  // 3) strip a single leading 0 if present
  if (p.startsWith("0")) p = p.slice(1);

  // 4) if still longer than 9, keep the last 9 (handles pasted long numbers)
  //if (p.length > 9) p = p.slice(-9);

  return p;
};

const EditAccountDetails = ({
  showModal,
  setShowModal,
  selectedData,
}: MyCarsEditProps) => {
  const [form] = Form.useForm();
  const { t, i18n } = useTranslation();
  const [base64Image, setBase64Image] = useState<any | null>(null);
  const [initialValues, setInitialValues] = useState<any>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string | any>(null);
  const [changedValuesState, setChangedValuesState] = useState<any>({});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [time_left, setTimeLeft] = useState(0);

  const { loading } = useSelector(
    (state: RootState) => state.login.EditProfile
  );
  const { countries } = useSelector((state: RootState) => state?.login);
  const { titles } = useSelector((state: RootState) => state?.login);

  const dispatch = useDispatch<AppDispatch>();
  const uploadProps = {
    name: "file",
    multiple: false,
    className: "avatar-uploader",
    showUploadList: false,
  };

  useEffect(() => {
    if (showModal && selectedData) {
      const formValues = {
        name: selectedData?.name || "",
        birthdate: selectedData?.birthdate
          ? moment(selectedData?.birthdate)
          : null,
        street: selectedData?.street || "",
        phone: selectedData?.phone || "",
        email: selectedData?.email || "",
        city: selectedData?.city || "",
        title: selectedData?.title || "",
        country_id: selectedData?.country_id || "",
        image_1920: selectedData?.image_1920 || "",
      };
      form.setFieldsValue(formValues);
      setInitialValues(formValues);
      setSelectedCountry(selectedData?.country_id);
    }
  }, [showModal, selectedData, form]);
  const handleFileChange = async (fileList: any[]) => {
    if (fileList.length === 0) return;

    const file = fileList[fileList.length - 1]?.originFileObj;
    if (file) {
      try {
        const base64 = await convertFileToBase64(file);

        setBase64Image(base64);
        form.setFieldsValue({ image_1920: base64 });
      } catch (error) {
        console.error("Error converting file to Base64:", error);
      }
    }
  };

  const getSelectValueByname = (name: string) => {
    const match = countries.list?.find((item: any) => item.name === name);
    return match?.id || "";
  };

  const getTitleValueByname = (name: string) => {
    const match = titles.list?.find((item: any) => item.name === name);
    return match?.id || "";
  };

  const handleApply = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();

      const normalizedValues = {
        ...values,
        birthdate: values.birthdate
          ? dayjs(values.birthdate).format("YYYY-MM-DD")
          : null,
      };

      const changedData: any = {};
      const otherChanges: any = {};

      for (const key in normalizedValues) {
        const initial = initialValues[key];
        const current = normalizedValues[key];

        if (key === "image_1920") {
          if (base64Image && base64Image !== initial) {
            changedData[key] = base64Image;
          }
        } else if (
          current !== (initial?.format ? initial.format("YYYY-MM-DD") : initial)
        ) {
          changedData[key] = current;
        }
      }

      if (changedData.title) {
        changedData.title = getTitleValueByname(changedData.title);
      }
      if (changedData.country_id) {
        changedData.country_id = getSelectValueByname(changedData.country_id);
      }

      // sanitize phone to 9 digits starting with 5 (local SA format) if present
      if (changedData.phone) {
        changedData.phone = sanitizeSaudiPhone(changedData.phone);
      }

      setChangedValuesState(changedData);
      // Check if email or phone changed
      const hasEmailChanged = "email" in changedData;
      const hasPhoneChanged = "phone" in changedData;

      let finalPayload = { ...changedData };

      if (hasEmailChanged || hasPhoneChanged) {
        finalPayload = {};
        if (hasEmailChanged) finalPayload.email = changedData.email;
        if (hasPhoneChanged) finalPayload.phone = changedData.phone;

        // Stock other fields separately (optional - just for your reference)
        for (const key in changedData) {
          if (key !== "email" && key !== "phone") {
            otherChanges[key] = changedData[key];
          }
        }
      }
      const success = (await dispatch(
        ToEditProfile({
          data: finalPayload,
          lang: i18n.language,
        })
      )) as any;
      const edited = success?.payload?.data?.result;

      if (edited?.code === 200 && !edited?.time_left) {
        showNotitfication(edited?.message, "success");
        dispatch(toGetProfil(i18n.language));
        setShowModal(false);
      } else if (edited?.time_left) {
        setTimeLeft(edited?.time_left);
        setIsModalVisible(true);
      } else {
        showNotitfication(edited?.message, "error");
      }
    } catch (error) {}
  };
  useEffect(() => {
    dispatch(toGetCountries(i18n.language));
    dispatch(toGetTitles(i18n.language));
  }, [dispatch, i18n.language]);
  const checkIfFormChanged = (_changedValues: any, allValues: any) => {
    const normalizedValues = {
      ...allValues,
      birthdate: allValues.birthdate
        ? dayjs(allValues.birthdate).format("YYYY-MM-DD")
        : null,
    };

    let changed = false;

    for (const key in normalizedValues) {
      const initial = initialValues[key];
      const current = normalizedValues[key];

      if (key === "image_1920") {
        if (base64Image && base64Image !== initial) {
          changed = true;
          break;
        }
      } else if (
        current !== (initial?.format ? initial.format("YYYY-MM-DD") : initial)
      ) {
        changed = true;
        break;
      }
    }

    setHasChanges(changed);
  };
  const handleCloseOtpModal = () => {
    setIsModalVisible(false);
  };
  return (
    <div>
      <ModalComponent
        title={
          <div className="ac--order-details-header">
            <span className="text-md-bold ac--color-text-profile">
              {t("profile.edit")}
            </span>
          </div>
        }
        open={showModal}
        onClose={() => setShowModal(false)}
        closeOnOutsideClick={true}
        width={500}
        modalStyle={{ top: 150 }}
        showCloseIcon={true}
      >
        <Form
          form={form}
          layout="vertical"
          className="mt-20"
          onFinish={handleApply}
          onValuesChange={checkIfFormChanged}
        >
          <div className="ac--cars-edit-container">
            <div className="profile-image-container">
              {base64Image || selectedData?.image_1920 ? (
                <img
                  src={
                    base64Image
                      ? `data:image/png;base64,${base64Image}`
                      : selectedData.image_1920
                  }
                  alt="avatar"
                  className="profile-image"
                />
              ) : (
                <div className="profile-image-placeholder">
                  <CameraOutlined />
                </div>
              )}
              <div className="edit-icon-container">
                <Form.Item
                  name="image_1920"
                  labelCol={{ className: "text-md-bold" }}
                >
                  <Upload
                    {...uploadProps}
                    showUploadList={false}
                    onChange={(info) => {
                      handleFileChange(info.fileList);
                    }}
                  >
                    <UploadOutlined className="edit-icon" />
                  </Upload>
                </Form.Item>
              </div>
            </div>
          </div>
          <Row gutter={[16, 16]} className="mt-20">
            <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
              <Form.Item
                label={t("profile.account.title")}
                name="title"
                labelCol={{ className: "text-md-bold" }}
              >
                <Select
                  virtual={false}
                  options={titles?.list?.map((option: any) => ({
                    label: option.name,
                    value: option.name,
                    id: option.id,
                  }))}
                  showSearch
                  size="large"
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 16]} className="mt-20">
            <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
              <Form.Item
                label={t("profile.account.fullname")}
                name="name"
                labelCol={{ className: "text-md-bold" }}
              >
                <input type="text" className="ac--input" />
              </Form.Item>
            </Col>
            <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
              <Form.Item
                label={t("profile.account.birthdate")}
                name="birthdate"
                labelCol={{ className: "text-md-bold" }}
              >
                <DatePicker
                  className="ac--input ac--input-date-picker"
                  format={"YYYY/MM/DD"}
                  getPopupContainer={(triggerNode) =>
                    triggerNode.parentElement as HTMLElement
                  }
                  onFocus={(e) => e.preventDefault()}
                />
              </Form.Item>
            </Col>

            <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
              <Form.Item
                label={t("profile.account.country")}
                labelCol={{ className: "text-md-bold" }}
                name="country_id"
              >
                <Select
                  virtual={false}
                  options={countries?.list
                    ?.filter(
                      (option: any) =>
                        option.name !== "Israel" && option.name !== "إسرائيل"
                    )
                    ?.slice(i18n.language === "ar" ? 12 : 0)
                    ?.map((option: any) => ({
                      label: option.name,
                      value: option.name,
                      id: option.id,
                    }))}
                  showSearch
                  size="large"
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  onChange={(value) => {
                    setSelectedCountry(value);
                    form.setFieldValue("country_id", value);
                    form.setFieldValue("city", "");
                  }}
                />
              </Form.Item>
            </Col>
            <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
              <Form.Item
                label={t("profile.account.city")}
                labelCol={{ className: "text-md-bold" }}
                name="city"
              >
                {selectedCountry === "المملكة العربية السعودية" ||
                selectedCountry === "Saudi Arabia" ? (
                  <Select
                    virtual={false}
                    showSearch
                    options={
                      selectedCountry === "المملكة العربية السعودية"
                        ? saudiCitiesAr
                        : saudiCitiesEn
                    }
                    size="large"
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                    onChange={(_, option) => {
                      form.setFieldValue(
                        "city",
                        (option as { label: string }).label
                      );
                    }}
                  />
                ) : (
                  <Input type="text" className="ac--input" />
                )}
              </Form.Item>
            </Col>

            <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
              <Form.Item
                label={t("profile.account.street")}
                name="street"
                labelCol={{ className: "text-md-bold" }}
              >
                <input type="text" className="ac--input" />
              </Form.Item>
            </Col>
            <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
              <Form.Item
                label={t("profile.account.phone")}
                name="phone"
                labelCol={{ className: "text-md-bold" }}
                required={false}
                rules={[
                  {
                    required: true,
                    message: t("otp.warning.phone"),
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const cleaned = sanitizeSaudiPhone(value);
                      if (/^5\d{8}$/.test(cleaned)) return Promise.resolve();
                      return Promise.reject(
                        new Error(t("register.invalidSaudiPhone"))
                      );
                    },
                  },
                ]}
              >
                <Input
                  type="tel"
                  className="ac--input"
                  addonBefore={i18n.language == "en" ? "+966" : null}
                  addonAfter={i18n.language == "ar" ? "966+" : null}
                  maxLength={12}
                  style={{ flex: 1 }}
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col xxl={24} xl={24} lg={24} md={24} sm={24} xs={24}>
              <Form.Item
                label={t("profile.account.email")}
                name="email"
                required={false}
                labelCol={{ className: "text-md-bold" }}
                rules={[
                  {
                    required: true,
                    type: "email",
                    message: t("contactUs.messages.emailRequired"),
                  },
                ]}
              >
                <Input type="email" className="ac--input" />
              </Form.Item>
            </Col>
          </Row>

          <Row className="mt-20" justify="end">
            <Button
              htmlType="submit"
              className="text-md ac--btn-edit-save"
              disabled={loading === true || !hasChanges}
            >
              {loading === true ? t("btn.loading") : t("btn.edit")}
            </Button>
          </Row>
        </Form>
      </ModalComponent>
      <EditProfileOtp
        open={isModalVisible}
        onClose={handleCloseOtpModal}
        data={changedValuesState}
        OtpTimeLeft={time_left}
        onSuccess={() => {
          handleCloseOtpModal();
          setShowModal(false);
        }}
      />
    </div>
  );
};

export default EditAccountDetails;
